import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Activity, Users, Clock } from "lucide-react";

const isInternalNetwork = () => {
  const hostname = window.location.hostname;
  return hostname === "testreact.home-k8s.lab";
};

const API_URL = isInternalNetwork()
  ? process.env.REACT_APP_API_URL_INTERNAL
  : process.env.REACT_APP_API_URL_EXTERNAL;

const StatCard = ({ title, value, icon: Icon, trend }) => (
  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-medium text-gray-400">{title}</h3>
      <Icon className="w-5 h-5 text-blue-400" />
    </div>
    <div className="flex items-baseline space-x-2">
      <span className="text-2xl font-bold text-gray-100">
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
      {trend !== undefined && (
        <span
          className={`text-sm ${trend > 0 ? "text-green-400" : "text-red-400"}`}
        >
          {trend > 0 ? "+" : ""}
          {trend}%
        </span>
      )}
    </div>
  </div>
);

const ApiUsageFilter = ({ onFilterChange, endpoints, currentFilters }) => (
  <div className="mb-4 flex flex-wrap gap-4">
    <select
      value={currentFilters.endpoint}
      onChange={(e) => onFilterChange("endpoint", e.target.value)}
      className="bg-gray-700 text-gray-100 rounded px-3 py-2 border border-gray-600"
    >
      <option value="">All Endpoints</option>
      {endpoints.map((endpoint) => (
        <option key={endpoint} value={endpoint}>
          {endpoint}
        </option>
      ))}
    </select>

    <select
      value={currentFilters.method}
      onChange={(e) => onFilterChange("method", e.target.value)}
      className="bg-gray-700 text-gray-100 rounded px-3 py-2 border border-gray-600"
    >
      <option value="">All Methods</option>
      <option value="GET">GET</option>
      <option value="POST">POST</option>
    </select>
  </div>
);

const AdminStats = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [registrationStats, setRegistrationStats] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(null);
  const [apiUsageData, setApiUsageData] = useState([]); // 原始数据
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    endpoint: "",
    method: "",
  });

  // 获取所有唯一的endpoints
  const getUniqueEndpoints = () => {
    return [...new Set(apiUsageData.map((item) => item.endpoint))].sort();
  };

  // 处理和分组API使用数据
  const getProcessedApiData = () => {
    // 首先应用筛选器
    const filteredData = apiUsageData.filter((item) => {
      const endpointMatch =
        !filters.endpoint || item.endpoint === filters.endpoint;
      const methodMatch = !filters.method || item.method === filters.method;
      return endpointMatch && methodMatch;
    });

    // 然后按照endpoint和method分组
    const groupedData = filteredData.reduce((acc, curr) => {
      const key = `${curr.endpoint}-${curr.method}`;
      if (!acc[key]) {
        acc[key] = {
          endpoint: curr.endpoint,
          method: curr.method,
          totalCalls: 0,
          counts: [],
        };
      }
      acc[key].totalCalls += curr.count;
      acc[key].counts.push(curr);
      return acc;
    }, {});

    return Object.values(groupedData);
  };

  // 处理筛选变化
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // 获取所有数据
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [regResponse, onlineResponse, apiResponse] = await Promise.all([
          fetch(`${API_URL}/api/stats/users/registrations`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          fetch(`${API_URL}/api/stats/users/online`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          fetch(`${API_URL}/api/stats/api/usage`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
        ]);

        const [regData, onlineData, apiData] = await Promise.all([
          regResponse.json(),
          onlineResponse.json(),
          apiResponse.json(),
        ]);

        setRegistrationStats(regData);
        setOnlineUsers(onlineData);
        setApiUsageData(apiData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load statistics");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const processedApiData = getProcessedApiData();
  const totalApiCalls = processedApiData.reduce(
    (sum, endpoint) => sum + endpoint.totalCalls,
    0
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-red-900/50 border border-red-800 text-red-200 p-4 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-100">
              Admin Dashboard
            </h1>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded transition-colors"
            >
              Back
            </button>
          </div>

          {isLoading ? (
            <div className="animate-pulse space-y-4">
              {/* ... loading skeleton ... */}
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard
                  title="Total Users"
                  value={registrationStats?.["1year"] || 0}
                  icon={Users}
                  trend={
                    registrationStats
                      ? (
                          (registrationStats["24hours"] /
                            registrationStats["7days"]) *
                            100 -
                          100
                        ).toFixed(1)
                      : 0
                  }
                />
                <StatCard
                  title="Online Users"
                  value={onlineUsers?.totalOnline || 0}
                  icon={Activity}
                />
                <StatCard
                  title="API Calls"
                  value={totalApiCalls}
                  icon={Clock}
                />
              </div>

              {/* API Usage Section */}
              <div className="bg-gray-800 rounded-lg border border-gray-700">
                <div className="p-4 border-b border-gray-700">
                  <h2 className="text-lg font-medium text-gray-100">
                    API Usage{" "}
                    {filters.endpoint
                      ? `- ${filters.endpoint}`
                      : "(All Endpoints)"}
                    {filters.method ? ` (${filters.method})` : ""}
                  </h2>
                </div>
                <div className="p-4">
                  <ApiUsageFilter
                    onFilterChange={handleFilterChange}
                    endpoints={getUniqueEndpoints()}
                    currentFilters={filters}
                  />

                  <div className="space-y-4">
                    {processedApiData.length > 0 ? (
                      processedApiData.map((endpointData) => (
                        <div
                          key={`${endpointData.endpoint}-${endpointData.method}`}
                          className="bg-gray-700/30 p-4 rounded-lg"
                        >
                          <h3 className="text-md font-medium text-blue-300 mb-2">
                            {endpointData.endpoint} ({endpointData.method})
                          </h3>
                          <p className="text-sm text-gray-400 mb-4">
                            Total Calls: {endpointData.totalCalls}
                          </p>
                          <div className="h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={endpointData.counts}>
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#374151"
                                />
                                <XAxis dataKey="timestamp" stroke="#9CA3AF" />
                                <YAxis stroke="#9CA3AF" />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "#1F2937",
                                    border: "1px solid #374151",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="count"
                                  stroke="#3B82F6"
                                  strokeWidth={2}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-400 py-8">
                        No data available for the selected filters
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
